interface StructuredError {
  message?: unknown
  details?: unknown
  hint?: unknown
  code?: unknown
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

/**
 * Supabase errors are plain objects rather than `Error` instances. Turning one
 * into a string directly only produces "[object Object]", which hides the
 * information needed to repair a failed synchronisation — and the badge is the
 * only place the user ever learns that a tick has not left the phone.
 *
 * `../semainier` also maps `PGRST204` onto the migration that repairs it. That
 * map names semainier's migration files, so it is deliberately not ported. The
 * message the user sees here still carries the column name and the code, which
 * is what `CLAUDE.md` §1 tells the *developer* to look up.
 */
export function formatSyncError(error: unknown): string {
  if (error instanceof Error) return error.message || error.name
  if (typeof error === 'string') return error

  if (error && typeof error === 'object') {
    const structured = error as StructuredError
    const message = nonEmptyString(structured.message)
    const details = nonEmptyString(structured.details)
    const hint = nonEmptyString(structured.hint)
    const code = nonEmptyString(structured.code)
    const parts = [message, details, hint].filter(
      (part, index, all): part is string => Boolean(part) && all.indexOf(part) === index,
    )

    if (parts.length) return `${parts.join(' — ')}${code ? ` (${code})` : ''}`

    try {
      return JSON.stringify(error)
    } catch {
      // A circular third-party object has no useful serialisation.
    }
  }

  return String(error)
}
